import { TiltLatestSiteData } from 'src/app/shared/interfaces/latest-site-data';
import { SensorHistory } from 'src/app/shared/interfaces/sensor-history';
import { UtilFunctions } from 'src/app/shared/utils/util-functions';

import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  Renderer2,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { LineChartComponent, LegendPosition } from '@swimlane/ngx-charts';

import {
  LineChartResultDto,
  LineChartResultSeriesDto,
} from '../../charts.interface';
import { ChartsService } from '../../charts.service';

@Component({
  selector: 'app-tilt-line-chart',
  templateUrl: './tilt-line-chart.component.html',
  styleUrls: ['./tilt-line-chart.component.scss'],
})
export class TiltLineChartComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() data: TiltLatestSiteData = {} as TiltLatestSiteData;
  @Input() chartRange: string = 'daytime';
  @ViewChild(LineChartComponent) chart: LineChartComponent | null = null;
  @ViewChild('chartContainer') chartContainer?: ElementRef<HTMLElement>;

  results: LineChartResultDto[] = [];

  // Line chart options
  view: [number, number] = [600, 400];
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = false;
  legendTitle = '';
  legendPosition = LegendPosition.Below;
  showXAxisLabel = false;
  xAxisLabel = 'Year';
  showYAxisLabel = false;
  yAxisLabel = 'Population';
  yScaleMin = 0;
  private readonly defaultYScaleMax = 50;
  private readonly yScaleMaxMargin = 10;
  yScaleMax = this.defaultYScaleMax;

  colorScheme = 'cool';
  lineColor = '#9C27B0';
  referenceLineColor = '#FF0000';
  customColors = (value: string) => {
    if (value === '管理基準値') {
      return this.referenceLineColor;
    }
    return this.lineColor;
  };

  timeline = false;

  sensorId = '';
  dataList: LineChartResultSeriesDto[] = [];
  private containerResizeObserver: ResizeObserver | null = null;
  private readonly chartWrapperPaddingLeft = 48;

  constructor(
    private chartsService: ChartsService,
    private renderer: Renderer2,
    private el: ElementRef
  ) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    this.sensorId = this.data.id;
    this.setChartWidth();

    const histories = await this.getHistories();
    this.clearAllData();
    this.createDataSets(histories);

    this.results = [];

    // 変位データを追加
    this.results.push({
      name: '変位',
      series: this.dataList,
    });

    this.updateYScaleMax();

    // 管理基準値の参照線を追加
    this.addReferenceLineToResults();

    if (this.chart) {
      this.chart.update();
    }
  }

  ngAfterViewInit() {
    // ツールチップの位置を調整
    this.adjustTooltipPosition();
    this.observeContainerWidth();
  }

  ngOnDestroy(): void {
    if (this.containerResizeObserver) {
      this.containerResizeObserver.disconnect();
      this.containerResizeObserver = null;
    }
  }

  adjustTooltipPosition() {
    // ツールチップ要素を監視して位置を調整
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const tooltips = this.el.nativeElement.querySelectorAll(
          '.nc-tooltip, .tooltip, .chart-tooltip, ngx-charts-tooltip'
        );
        tooltips.forEach((tooltip: any) => {
          if (tooltip && tooltip.style) {
            const currentTop = parseInt(tooltip.style.top || '0', 10);
            tooltip.style.top = `${currentTop - 20}px`;
          }
        });
      });
    });

    // チャート要素を監視
    const chartElement = this.el.nativeElement.querySelector(
      'ngx-charts-line-chart'
    );
    if (chartElement) {
      observer.observe(chartElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }
  }

  onResize(event: UIEvent) {
    this.setChartWidth();
  }

  setChartWidth() {
    const container = this.getChartContainer();

    if (container) {
      const availableWidth = Math.max(
        container.clientWidth - this.getContainerPaddingRight(container),
        200
      );
      this.view = [availableWidth, this.view[1]];
      return;
    }

    const fallbackWidth = this.chartsService.getChartWidthFromWindowWidth(
      window.innerWidth
    );
    this.view[0] = Math.max(fallbackWidth, 200);
  }

  onSelect(event: any) {}

  formatValue(value: number | null | undefined): string {
    if (value == null || isNaN(value)) {
      return '-';
    }
    return value.toLocaleString('ja-JP', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  }

  private observeContainerWidth() {
    const container = this.getChartContainer();

    if (!container || typeof ResizeObserver === 'undefined') {
      this.setChartWidth();
      return;
    }

    if (this.containerResizeObserver) {
      this.containerResizeObserver.disconnect();
    }

    this.containerResizeObserver = new ResizeObserver(() => {
      this.setChartWidth();
    });
    this.containerResizeObserver.observe(container);
    this.setChartWidth();
  }

  private getChartContainer(): HTMLElement | null {
    if (this.chartContainer?.nativeElement) {
      return this.chartContainer.nativeElement;
    }

    const hostElement = this.el.nativeElement as HTMLElement;
    return hostElement.parentElement;
  }

  private getContainerPaddingRight(container: HTMLElement): number {
    const styles = getComputedStyle(container);
    const paddingRight = parseFloat(styles.paddingRight || '0');

    return paddingRight;
  }

  async getHistories() {
    let histories: SensorHistory[] | null = null;
    if (this.chartRange === '' || this.chartRange === 'daytime') {
      histories = await this.chartsService.getDaytimeSensorHistory(
        this.sensorId
      );
    } else if (this.chartRange === 'day') {
      histories = await this.chartsService.getDaySensorHistory(this.sensorId);
    } else if (this.chartRange === 'week') {
      histories = await this.chartsService.getWeekSensorHistory(this.sensorId);
    } else if (this.chartRange === 'month') {
      histories = await this.chartsService.getMonthSensorHistory(this.sensorId);
    }

    return histories;
  }

  createDataSets(histories: SensorHistory[] | null) {
    if (histories === null) return;

    const result = this.chartsService.createTiltChartDataSets(
      this.chartRange,
      histories
    );
    if (result !== undefined) {
      this.dataList = result.displacement;
    }
  }

  clearAllData() {
    this.dataList = [];
  }

  addReferenceLineToResults() {
    // 管理基準値が存在する場合のみ参照線を追加
    if (
      this.data.managementStandard !== undefined &&
      this.data.managementStandard !== null
    ) {
      // データセットの最初と最後の時間を取得
      if (this.dataList.length > 0) {
        const firstTime = this.dataList[0].name;
        const lastTime = this.dataList[this.dataList.length - 1].name;

        // 参照線データを作成
        const referenceLineData: LineChartResultSeriesDto[] = [
          {
            name: firstTime,
            value: this.data.managementStandard,
          },
          {
            name: lastTime,
            value: this.data.managementStandard,
          },
        ];

        // 管理基準値の系列を結果に追加
        this.results.push({
          name: '管理基準値',
          series: referenceLineData,
        });
      }
    }
  }

  private updateYScaleMax() {
    const maxDisplacement = this.getMaxDisplacement();
    if (maxDisplacement !== null && maxDisplacement > this.defaultYScaleMax) {
      this.yScaleMax = maxDisplacement + this.yScaleMaxMargin;
      return;
    }

    this.yScaleMax = this.defaultYScaleMax;
  }

  private getMaxDisplacement(): number | null {
    let maxDisplacement: number | null = null;

    this.dataList.forEach(({ value }) => {
      if (!Number.isFinite(value)) {
        return;
      }

      maxDisplacement =
        maxDisplacement === null ? value : Math.max(maxDisplacement, value);
    });

    return maxDisplacement;
  }
}
